import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord, faGithub } from '@fortawesome/free-brands-svg-icons';
import { faHeart, faRobot, faPlus } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/GlobalStyles';

const FooterContainer = styled.footer`
  background-color: rgba(74, 74, 74, 0.8);
  backdrop-filter: blur(${theme.glassMorphism.blur});
  -webkit-backdrop-filter: blur(${theme.glassMorphism.blur});
  padding: 60px 0 40px;
  margin-top: 60px;
  border-top: ${theme.glassMorphism.border};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${theme.colors.primary}40, transparent);
  }
  
  @media (max-width: 768px) {
    padding: 40px 0 30px;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: ${theme.colors.secondary};
    margin-bottom: 20px;
    font-size: 1.2rem;
    position: relative;
    padding-bottom: 10px;
    
    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 40px;
      height: 3px;
      background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
      border-radius: 2px;
    }
  }
  
  @media (max-width: 768px) {
    text-align: center;
    
    h3:after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  
  li {
    margin-bottom: 12px;
  }
  
  a {
    color: ${theme.colors.textSecondary};
    transition: all 0.3s ease;
    display: inline-block;
    
    &:hover {
      color: ${theme.colors.primary};
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    
    a:hover {
      transform: translateY(-2px);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
  
  a {
    color: ${theme.colors.textSecondary};
    font-size: 1.5rem;
    transition: all 0.3s ease;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: ${theme.colors.primary};
      background-color: rgba(255, 255, 255, 0.1);
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(255, 107, 107, 0.2);
    }
  }
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 40px;
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
  max-width: 1200px;
  margin: 0 auto;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  
  a {
    color: ${theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    padding-top: 30px;
  }
`;

const HeartIcon = styled(FontAwesomeIcon)`
  color: ${theme.colors.primary};
  margin: 0 5px;
`;

// Añadir un botón destacado para invitar al bot
const InviteButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #5865F2, #7289DA);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  margin-top: 15px;
  transition: all 0.3s ease;
  border: none;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(88, 101, 242, 0.4);
  }
  
  @media (max-width: 768px) {
    margin: 15px auto;
    max-width: 220px;
  }
`;

const Footer = () => {
  const BOT_INVITE_URL = "https://discord.com/oauth2/authorize?client_id=1380674741685321758&permissions=8&redirect_uri=https%3A%2F%2Flpxmpqpmoofsrlbenatl.supabase.co%2Fauth%2Fv1%2Fcallback&integration_type=0&scope=bot";
  const DISCORD_SERVER_URL = "https://discord.gg/pxHEVKftmg";
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>TuSecreto Bot</h3>
          <p style={{ color: theme.colors.textSecondary }}>
            Un bot de Discord para compartir tus secretos de forma anónima, con reacciones y comentarios.
          </p>
          <InviteButton 
            href={BOT_INVITE_URL} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faPlus} /> Añadir Bot a Discord
          </InviteButton>
          <SocialLinks>
            <a href={DISCORD_SERVER_URL} target="_blank" rel="noopener noreferrer" aria-label="Discord">
              <FontAwesomeIcon icon={faDiscord} />
            </a>
            <a href="https://github.com/tusecreto/bot" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FontAwesomeIcon icon={faGithub} />
            </a>
          </SocialLinks>
        </FooterSection>
        
        <FooterSection>
          <h3>Enlaces</h3>
          <FooterLinks>
            <li><Link to="/secretos">Secretos</Link></li>
            <li><Link to="/inicio">Inicio</Link></li>
            <li><Link to="/comandos">Comandos</Link></li>
            <li><Link to="/guia">Guía</Link></li>
            <li><Link to="/terminos">Términos y Condiciones</Link></li>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <h3>Comunidad</h3>
          <FooterLinks>
            <li>
              <a 
                href={DISCORD_SERVER_URL} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faDiscord} style={{ marginRight: "8px" }} /> 
                Servidor de Discord
              </a>
            </li>
            <li>
              <a 
                href={BOT_INVITE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faRobot} style={{ marginRight: "8px" }} /> 
                Añadir a tu servidor
              </a>
            </li>
          </FooterLinks>
        </FooterSection>
      </FooterContent>
      
      <Copyright>
        <p>&copy; {new Date().getFullYear()} TuSecreto Bot. Desarrollado con <HeartIcon icon={faHeart} /> en Argentina. <Link to="/terminos">Términos y Condiciones</Link></p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer; 